import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import styles from './VehicleImageCarousel.module.css';

function getVehicleKey(vehicle) {
  return String(
    vehicle?.imei ||
    vehicle?.uniqueId ||
    vehicle?.id ||
    vehicle?.name ||
    ''
  );
}

function getVehicleImage(vehicle) {
  return (
    vehicle?.image_url ||
    vehicle?.vehicle_image_url ||
    vehicle?.photo_url ||
    vehicle?.photo ||
    vehicle?.image ||
    ''
  );
}
function getDemoVehicleImage(index) {
  const demoImages = [
    '/vehicles-demo/swift.jpg',
    '/vehicles-demo/jimny.webp',
    '/vehicles-demo/hilux.webp'
  ];

  return demoImages[index] || '';
}


function VehicleImageCarousel({
  vehicles = [],
  selectedVehicle,
  onSelectVehicle
}) {
  const carouselRef = useRef(null);
  const cardRefs = useRef([]);
  const scrollTimerRef = useRef(null);
  const animationFrameRef = useRef(null);
  const selectionFromScrollRef = useRef(false);
  const gestureStartXRef = useRef(null);
const gestureMovedRef = useRef(false);

  const selectedKey = useMemo(
    () => getVehicleKey(selectedVehicle),
    [selectedVehicle]
  );

  const initialIndex = useMemo(() => {
    if (!selectedKey) {
      return 0;
    }

    const index = vehicles.findIndex(
      (vehicle) =>
        getVehicleKey(vehicle) === selectedKey
    );

    return index >= 0 ? index : 0;
  }, [selectedKey, vehicles]);

  const [activeIndex, setActiveIndex] =
    useState(initialIndex);

  const selectVehicle = useCallback(
    (index) => {
      const vehicle = vehicles[index];

      if (!vehicle) {
        return;
      }

      setActiveIndex(index);
      onSelectVehicle?.(vehicle);
    },
    [onSelectVehicle, vehicles]
  );

  const scrollToVehicle = useCallback(
    (index, behavior = 'smooth') => {
      const card = cardRefs.current[index];

      if (!card) {
        return;
      }

      card.scrollIntoView({
        behavior,
        block: 'nearest',
        inline: 'center'
      });
    },
    []
  );

useEffect(() => {
  setActiveIndex(initialIndex);

  /*
    Cuando la selección fue producida por el mismo
    desplazamiento, no volvemos a mover el carrusel.
    Esto evita el salto o efecto de patinaje.
  */
  if (selectionFromScrollRef.current) {
    selectionFromScrollRef.current = false;
    return;
  }

  window.requestAnimationFrame(() => {
    scrollToVehicle(
      initialIndex,
      'auto'
    );
  });
}, [
  initialIndex,
  scrollToVehicle
]);

  useEffect(() => {
    return () => {
      if (scrollTimerRef.current) {
        window.clearTimeout(
          scrollTimerRef.current
        );
      }

      if (animationFrameRef.current) {
        window.cancelAnimationFrame(
          animationFrameRef.current
        );
      }
    };
  }, []);

  const findCenteredIndex = useCallback(() => {
    const carousel = carouselRef.current;

    if (!carousel || vehicles.length === 0) {
      return 0;
    }

    const carouselRect =
      carousel.getBoundingClientRect();

    const carouselCenter =
      carouselRect.left +
      carouselRect.width / 2;

    let closestIndex = 0;
    let closestDistance = Infinity;

    cardRefs.current.forEach(
      (card, index) => {
        if (!card) {
          return;
        }

        const cardRect =
          card.getBoundingClientRect();

        const cardCenter =
          cardRect.left +
          cardRect.width / 2;

        const distance = Math.abs(
          carouselCenter - cardCenter
        );

        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      }
    );

    return closestIndex;
  }, [vehicles.length]);

const handleScroll = useCallback(() => {
  /*
    Actualizamos inmediatamente cuál tarjeta
    se ve como principal.

    Así no desaparecen las tarjetas mientras
    el usuario está deslizando.
  */
  if (animationFrameRef.current) {
    window.cancelAnimationFrame(
      animationFrameRef.current
    );
  }

  animationFrameRef.current =
    window.requestAnimationFrame(() => {
      const centeredIndex =
        findCenteredIndex();

      setActiveIndex((currentIndex) => {
        if (currentIndex === centeredIndex) {
          return currentIndex;
        }

        return centeredIndex;
      });
    });

  /*
    La selección real del vehículo se realiza
    cuando el movimiento termina.

    Este temporizador no controla la apariencia,
    solamente actualiza el activo seleccionado.
  */
  if (scrollTimerRef.current) {
    window.clearTimeout(
      scrollTimerRef.current
    );
  }

  scrollTimerRef.current =
    window.setTimeout(() => {
      const centeredIndex =
        findCenteredIndex();

      const vehicle =
        vehicles[centeredIndex];

      if (!vehicle) {
        return;
      }

      selectionFromScrollRef.current = true;
      onSelectVehicle?.(vehicle);
    }, 110);
}, [
  findCenteredIndex,
  onSelectVehicle,
  vehicles
]);
const moveOneVehicle = useCallback(
  (direction) => {
    if (vehicles.length === 0) {
      return;
    }

    const nextIndex = Math.max(
      0,
      Math.min(
        vehicles.length - 1,
        activeIndex + direction
      )
    );

    if (nextIndex === activeIndex) {
      scrollToVehicle(activeIndex);
      return;
    }

    const vehicle = vehicles[nextIndex];

    if (!vehicle) {
      return;
    }

    setActiveIndex(nextIndex);
    onSelectVehicle?.(vehicle);
    scrollToVehicle(nextIndex);
  },
  [
    activeIndex,
    onSelectVehicle,
    scrollToVehicle,
    vehicles
  ]
);

const handlePointerDown = useCallback(
  (event) => {
    gestureStartXRef.current =
      event.clientX;

    gestureMovedRef.current = false;

    event.currentTarget.setPointerCapture?.(
      event.pointerId
    );
  },
  []
);

const handlePointerMove = useCallback(
  (event) => {
    if (gestureStartXRef.current == null) {
      return;
    }

    const distance =
      event.clientX -
      gestureStartXRef.current;

    if (Math.abs(distance) > 8) {
      gestureMovedRef.current = true;
    }
  },
  []
);

const finishPointerGesture = useCallback(
  (event) => {
    if (gestureStartXRef.current == null) {
      return;
    }

    const distance =
      event.clientX -
      gestureStartXRef.current;

    gestureStartXRef.current = null;

    event.currentTarget.releasePointerCapture?.(
      event.pointerId
    );

    /*
      Un movimiento horizontal completo
      avanza solamente una tarjeta.
    */
    if (Math.abs(distance) < 35) {
      return;
    }

    if (distance < 0) {
      moveOneVehicle(1);
    } else {
      moveOneVehicle(-1);
    }
  },
  [moveOneVehicle]
);

const cancelPointerGesture = useCallback(
  () => {
    gestureStartXRef.current = null;
    gestureMovedRef.current = false;
  },
  []
);
const handleCardClick = useCallback(
  (vehicle, index) => {
    /*
      Evita que al terminar un deslizamiento
      también se ejecute el clic.
    */
    if (gestureMovedRef.current) {
      gestureMovedRef.current = false;
      return;
    }

    setActiveIndex(index);
    onSelectVehicle?.(vehicle);
    scrollToVehicle(index);
  },
  [
    onSelectVehicle,
    scrollToVehicle
  ]
);

  if (!Array.isArray(vehicles)) {
    return null;
  }

  if (vehicles.length === 0) {
    return null;
  }

  return (
    <section
      className={styles.wrapper}
      data-app-swipe-ignore="true"
      aria-label="Activos disponibles"
    >
    <div
  ref={carouselRef}
  className={styles.carousel}
  onPointerDown={handlePointerDown}
  onPointerMove={handlePointerMove}
  onPointerUp={finishPointerGesture}
  onPointerCancel={cancelPointerGesture}
>
        {vehicles.map(
          (vehicle, index) => {
            const vehicleKey =
              getVehicleKey(vehicle);

        const imageUrl =
  getVehicleImage(vehicle) ||
  getDemoVehicleImage(index);

            const name =
              vehicle.name ||
              vehicle.plate ||
              'Activo';

            const initial =
              String(name)
                .trim()
                .charAt(0)
                .toUpperCase();

            const distance =
              index - activeIndex;

            const isActive =
              distance === 0;

            const isPrevious =
              distance === -1;

            const isNext =
              distance === 1;

            const isFar =
              Math.abs(distance) > 1;

            return (
              <button
                key={vehicleKey || index}
                ref={(element) => {
                  cardRefs.current[index] =
                    element;
                }}
                type="button"
                className={[
                  styles.card,
                  isActive
                    ? styles.activeCard
                    : '',
                  isPrevious
                    ? styles.previousCard
                    : '',
                  isNext
                    ? styles.nextCard
                    : '',
                  isFar
                    ? styles.farCard
                    : ''
                ]
                  .filter(Boolean)
                  .join(' ')}
                aria-current={
                  isActive
                    ? 'true'
                    : undefined
                }
                aria-label={`Seleccionar ${name}`}
                onClick={() => {
                  handleCardClick(
                    vehicle,
                    index
                  );
                }}
              >
        {imageUrl ? (
  <>
    <img
      src={imageUrl}
      alt=""
      className={styles.imageBackdrop}
      loading="lazy"
      decoding="async"
      draggable="false"
      aria-hidden="true"
    />

    <img
      src={imageUrl}
      alt={name}
      className={styles.image}
      loading="lazy"
      decoding="async"
      draggable="false"
    />
  </>
) : (
                  <div
                    className={
                      styles.imageFallback
                    }
                    aria-hidden="true"
                  >
                    <span
                      className={
                        styles.fallbackInitial
                      }
                    >
                      {initial}
                    </span>

                    <span
                      className={
                        styles.fallbackLabel
                      }
                    >
                      Imagen del activo
                    </span>
                  </div>
                )}

                <span
                  className={styles.imageShade}
                  aria-hidden="true"
                />

                <span
                  className={styles.cardContent}
                >
                  <strong
                    className={styles.vehicleName}
                  >
                    {name}
                  </strong>

                  {isActive && (
                    <span
                      className={
                        styles.selectedStatus
                      }
                    >
                      <span
                        className={
                          styles.selectedDot
                        }
                      />

                      Activo seleccionado
                    </span>
                  )}
                </span>

                <span
                  className={styles.bottomAccent}
                  aria-hidden="true"
                />
              </button>
            );
          }
        )}
      </div>
    </section>
  );
}

export default VehicleImageCarousel;